import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Minus, Plus, Package, BarChart2,
  Loader2, AlertCircle, Info, Warehouse, TrendingUp, Users, Pencil
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ProductImage from '@/components/products/ProductImage';
import StockBadge from '@/components/products/StockBadge';
import { toast } from 'sonner';
import { api, getBackend } from '@/api/apiClient';
import { useRepProfile } from '@/lib/RepProfileContext';
import { cur } from '@/lib/currency';

const TABS = [
  { key: 'details', label: 'التفاصيل', icon: Info },
  { key: 'stock',   label: 'المخزون',  icon: Warehouse },
  { key: 'sales',   label: 'المبيعات', icon: TrendingUp },
];

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile: repProfile, isManager } = useRepProfile();
  // Products are edited in the app on SellGo's own database only.
  const canEdit = getBackend() === 'supabase' && (isManager || !!repProfile.allow_add_product);
  const [product, setProduct] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('details');
  const [qty, setQty] = useState(1);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/products/${parseInt(id)}`);
      if (res?.success) {
        const p = res.data;
        setProduct(p);
        // Load similar products
        if (p.categ_id) {
          const categId = Array.isArray(p.categ_id) ? p.categ_id[0] : p.categ_id;
          const simRes = await api.get('/products', {
            categ_id: categId, exclude_id: p.id
          });
          if (simRes?.success) setSimilar(simRes.data || []);
        }
      } else {
        throw new Error(res?.error || 'فشل تحميل المنتج');
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground">جاري تحميل المنتج...</p>
    </div>
  );

  if (error) return (
    <div className="p-6 flex flex-col items-center gap-4">
      <AlertCircle className="w-12 h-12 text-red-500" />
      <p className="font-semibold text-foreground text-center">{error}</p>
      <Button onClick={load} variant="outline">إعادة المحاولة</Button>
    </div>
  );

  if (!product) return null;

  const uom = Array.isArray(product.uom_id) ? product.uom_id[1] : (product.uom_id || 'قطعة');
  const categ = product.categ_id;
  const totalPrice = (product.list_price || 0) * qty;

  const handleAddToOrder = () => {
    navigate(`/orders/new?product_id=${product.id}&product_name=${encodeURIComponent(product.name)}&qty=${qty}`);
  };

  return (
    <div className="flex flex-col min-h-full pb-32" dir="rtl">
      {/* Product image */}
      <div className="bg-white px-6 pt-4 pb-2">
        <div className="max-w-xs mx-auto">
          <ProductImage src={product.image_src} categ={categ} size="detail" />
        </div>
      </div>

      {/* Product info */}
      <div className="px-4 pt-4 space-y-3">
        {/* Name + code */}
        <div className={canEdit ? 'relative pl-24' : 'relative'}>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={() => navigate(`/products/${product.id}/edit`)}
              className="absolute left-0 top-0 gap-1.5 rounded-xl">
              <Pencil className="w-3.5 h-3.5" /> تعديل
            </Button>
          )}
          {product.default_code && (
            <p className="text-xs text-muted-foreground font-mono mb-1">كود: {product.default_code}</p>
          )}
          {product.barcode && (
            <p className="text-xs text-muted-foreground font-mono mb-1">باركود: {product.barcode}</p>
          )}
          <h1 className="text-xl font-bold text-foreground leading-snug">{product.name}</h1>
          {categ && (
            <p className="text-sm text-muted-foreground mt-1">{Array.isArray(categ) ? categ[1] : categ}</p>
          )}
        </div>

        {/* Price + Stock row */}
        <div className="flex items-center justify-between gap-3 bg-card border border-border rounded-2xl p-4">
          <div>
            <p className="text-xs text-muted-foreground">سعر البيع</p>
            <p className="text-2xl font-bold text-primary">
              {Number(product.list_price || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              <span className="text-sm font-normal mr-1">{cur()} / {uom}</span>
            </p>
          </div>
          <StockBadge qty={product.qty_available} size="md" />
        </div>

        {/* Quantity selector */}
        <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
          <p className="text-sm font-semibold text-foreground">الكمية</p>
          <div className="flex items-center gap-3">
            <button onClick={() => setQty(q => Math.max(1, q - 1))}
              className="w-10 h-10 bg-muted rounded-xl flex items-center justify-center hover:bg-muted/70 transition-colors">
              <Minus className="w-4 h-4" />
            </button>
            <Input
              type="number" value={qty}
              onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v) && v > 0) setQty(v); }}
              className="w-20 h-10 text-center text-lg font-bold p-1 rounded-xl"
              min={1}
            />
            <button onClick={() => setQty(q => q + 1)}
              className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center hover:bg-primary/90 transition-colors">
              <Plus className="w-4 h-4 text-white" />
            </button>
            <span className="text-sm text-muted-foreground">{uom}</span>
          </div>
          {/* Quick qty presets */}
          <div className="flex gap-2">
            {[1, 5, 10, 24].map(n => (
              <button key={n} onClick={() => setQty(n)}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-xl border transition-all ${qty === n ? 'bg-primary text-white border-primary' : 'bg-muted border-transparent text-muted-foreground hover:border-primary/30'}`}>
                {n}+
              </button>
            ))}
          </div>
          <div className="flex justify-between items-center pt-1">
            <span className="text-sm text-muted-foreground">إجمالي</span>
            <span className="text-lg font-bold text-primary">{totalPrice.toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {cur()}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold border-b-2 transition-all ${
                  tab === t.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}>
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="space-y-3 pb-4">
          {/* Details */}
          {tab === 'details' && (
            <div className="space-y-3">
              {product.description_sale && (
                <div className="bg-card border border-border rounded-2xl p-4">
                  <p className="text-xs font-semibold text-muted-foreground mb-2">وصف المنتج</p>
                  <p className="text-sm text-foreground leading-relaxed">{product.description_sale}</p>
                </div>
              )}
              <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground">المواصفات</p>
                {[
                  { label: 'وحدة القياس', value: uom },
                  { label: 'الفئة', value: Array.isArray(categ) ? categ[1] : categ },
                  { label: 'الوزن', value: product.weight ? `${product.weight} كجم` : null },
                  { label: 'الحجم', value: product.volume ? `${product.volume} لتر` : null },
                  { label: 'الكود', value: product.default_code },
                  { label: 'الباركود', value: product.barcode },
                ].filter(r => r.value).map(r => (
                  <div key={r.label} className="flex justify-between items-center border-b border-border last:border-0 pb-2 last:pb-0">
                    <span className="text-xs text-muted-foreground">{r.label}</span>
                    <span className="text-sm font-medium text-foreground">{r.value}</span>
                  </div>
                ))}
              </div>

              {/* Similar products */}
              {similar.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-bold text-foreground">منتجات مشابهة</p>
                  <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                    {similar.map(p => (
                      <div key={p.id}
                        onClick={() => navigate(`/products/${p.id}`)}
                        className="flex-shrink-0 w-32 bg-card border border-border rounded-xl p-2 cursor-pointer hover:border-primary/30 transition-all"
                      >
                        <p className="text-xs font-semibold text-foreground line-clamp-2 leading-tight">{p.name}</p>
                        <p className="text-xs font-bold text-primary mt-1">{Number(p.list_price || 0).toFixed(2)} {cur()}</p>
                        <StockBadge qty={p.qty_available} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Stock */}
          {tab === 'stock' && (
            <div className="space-y-3">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-card border border-border rounded-2xl p-3 text-center">
                  <p className="text-2xl font-bold text-primary">{Number(product.qty_available || 0).toLocaleString('ar-EG', { maximumFractionDigits: 0 })}</p>
                  <p className="text-xs text-muted-foreground">متاح فعلياً</p>
                </div>
                <div className="bg-card border border-border rounded-2xl p-3 text-center">
                  <p className="text-2xl font-bold text-foreground">{Number(product.virtual_available || 0).toLocaleString('ar-EG', { maximumFractionDigits: 0 })}</p>
                  <p className="text-xs text-muted-foreground">متوقع</p>
                </div>
              </div>

              {/* Per location */}
              {product.stock_details?.length > 0 ? (
                <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
                  <p className="text-sm font-bold text-foreground">المخازن</p>
                  {product.stock_details.map((loc, i) => (
                    <div key={i} className="flex justify-between items-start border-b border-border last:border-0 pb-2 last:pb-0">
                      <p className="text-sm text-foreground flex-1 ml-2">{loc.location}</p>
                      <div className="text-left flex-shrink-0">
                        <p className="text-sm font-bold text-primary">{Number(loc.available).toLocaleString('ar-EG', { maximumFractionDigits: 0 })}</p>
                        <p className="text-xs text-muted-foreground">محجوز: {Number(loc.reserved).toLocaleString('ar-EG', { maximumFractionDigits: 0 })}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-muted/50 border border-border rounded-2xl p-6 text-center">
                  <Warehouse className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">لا توجد بيانات مخزن تفصيلية</p>
                </div>
              )}
            </div>
          )}

          {/* Sales */}
          {tab === 'sales' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-primary/5 border border-primary/20 rounded-2xl p-3 text-center">
                  <p className="text-xl font-bold text-primary">{Number(product.total_sold_qty || 0).toLocaleString('ar-EG', { maximumFractionDigits: 0 })}</p>
                  <p className="text-xs text-muted-foreground">إجمالي الكميات المباعة</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3 text-center">
                  <p className="text-lg font-bold text-emerald-700">{Number(product.total_sold_revenue || 0).toLocaleString('ar-EG', { maximumFractionDigits: 0 })} {cur()}</p>
                  <p className="text-xs text-muted-foreground">إجمالي الإيرادات</p>
                </div>
              </div>
              {!product.total_sold_qty && (
                <div className="bg-muted/50 border border-border rounded-2xl p-6 text-center">
                  <BarChart2 className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">لا توجد مبيعات مسجلة خلال آخر 12 شهر</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sticky add to order bar */}
      <div className="fixed bottom-0 right-0 left-0 bg-card border-t border-border p-4 z-30">
        <Button onClick={handleAddToOrder} className="w-full bg-primary gap-2 h-12 text-base font-bold shadow-lg shadow-primary/30">
          <ShoppingCart className="w-5 h-5" />
          إضافة للطلب • {totalPrice.toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {cur()}
        </Button>
      </div>
    </div>
  );
}